/* -------------------------------------------------------------------------- */
/*                         SlidingPaneStartUnitDowntime                       */
/* -------------------------------------------------------------------------- */
// Componente que permite iniciar el estado downtime a una unidad seleccionada

import { Fragment, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import SlidingPane from 'react-sliding-pane';
import { Button, Col, Row } from 'reactstrap';
import DefaultButton from 'views/components/DefaultButton';
import Toolbar from 'views/components/Toolbar';
import DropdownInput from 'views/formComponents/DropdownInput';
import useGetDowntimeReasons from 'views/screens/Organizations/hooks/useGetDowntimeReasons';
import * as organizationSelectors from 'views/screens/Organizations/reducers';
import unitPermissions from 'views/screens/TrackingUnits/permissions';
import * as unitActions from '../../screens/Units/actions';
import SlidingPaneDowntimeReasons from '../SlidingPaneDowntimeReasons';

interface Props {
  setDowntime?: (value) => void;
  closeSlidingPane: (value) => void;
  isSlidingPaneOpen: boolean;
  unit: any;
  organization: any;
  width?: string;
}

const SlidingPaneStartUnitDowntime = (props: Props) => {
  const { closeSlidingPane, setDowntime, isSlidingPaneOpen, unit, organization, width } = props;

  //HOOKS
  const { control, watch, handleSubmit, reset } = useForm();
  const intl = useIntl();
  const dispatch = useDispatch();
  const formValues = watch();

  const [downtimeReasonsSlideOpen, openDowntimeReasonsSlide] = useState(false);
  const [executeGetDowntimeReasons, setExecuteGetDowntimeReasons] = useState(false);

  const { isOperating } = useSelector((state) => ({
    isOperating:
      organizationSelectors.isRemovingOrganizationDowntimeReason(state) ||
      organizationSelectors.isEditingOrganizationDowntimeReason(state) ||
      organizationSelectors.isAddingOrganizationDowntimeReason(state)
  }));

  const { downtimeReasons, isFetchingDowntimeReasons } = useGetDowntimeReasons(
    unit?.organizationId,
    executeGetDowntimeReasons
  );

  const mainStates = [...Array(7).keys()].map((id) => ({
    id: id,
    value: id,
    label: intl.formatMessage({ id: `units.mainStateId.${id}` }),
    label_intl: intl.formatMessage({ id: `units.mainStateId.${id}` })
  }));

  const onSubmit = () => {
    dispatch(unitActions.startStartDowntimeUnit(unit?.unitId, formValues?.subMainStateId));
    if (setDowntime)
      setDowntime({
        downtimeReasonId: formValues?.subMainStateId,
        downtimeReasonDescription: downtimeReasons.filter(
          (reason) => reason.reasonId === formValues?.subMainStateId
        )[0]?.reasonDescription
      });
    reset();
    closeSlidingPane(true);
  };

  useEffect(() => {
    reset({
      ...formValues,
      mainStateId: 6
    });
  }, []);

  useEffect(() => {
    isOperating === false && isFetchingDowntimeReasons === false
      ? setExecuteGetDowntimeReasons(!executeGetDowntimeReasons)
      : '';
  }, [isOperating]);

  useEffect(() => {
    organization?.organizationId !== undefined
      ? setExecuteGetDowntimeReasons(!executeGetDowntimeReasons)
      : '';
  }, [organization?.organizationId]);

  return unit !== undefined && unit !== null ? (
    <SlidingPane
      className="custom-sliding-pane"
      overlayClassName={'custom-overlay-sliding-pane-map'}
      isOpen={isSlidingPaneOpen}
      title={intl.formatMessage({ id: 'units.startDowntime' })}
      subtitle={unit?.unitName}
      width={width ?? '35rem'}
      onRequestClose={() => {
        if (closeSlidingPane) closeSlidingPane(false);
      }}
    >
      <Fragment>
        <Toolbar
          buttons={[
            <DefaultButton
              id={'downtimeReasonsButton'}
              icon={'DowntimeReason'}
              color="secondary"
              permissions={[
                unitPermissions.addDowntimeReasons,
                unitPermissions.editDowntimeReasons,
                unitPermissions.removeDowntimeReasons
              ]}
              handleClick={() => {
                openDowntimeReasonsSlide(true);
              }}
              tooltipMessage={intl.formatMessage({ id: 'organizations.showDowntimeReasons' })}
            />
          ]}
        />
        <Row>
          <Col sm="12">
            <Controller
              control={control}
              name="mainStateId"
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <DropdownInput
                  field={field}
                  fieldstate={fieldState}
                  id={'mainStateDropdown'}
                  label={intl.formatMessage({ id: 'units.status' })}
                  options={mainStates}
                  isDisabled
                />
              )}
            />
          </Col>
          {formValues?.mainStateId === 6 && (
            <Col sm="12">
              <Controller
                control={control}
                name="subMainStateId"
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <DropdownInput
                    field={field}
                    fieldstate={fieldState}
                    id={'subMainStateDropdown'}
                    label={intl.formatMessage({ id: 'units.reason' })}
                    options={downtimeReasons.map((downtimeReason) => ({
                      id: downtimeReason.reasonId,
                      value: downtimeReason.reasonId,
                      label: downtimeReason.reasonDescription,
                      label_intl: downtimeReason.reasonDescription
                    }))}
                    required={true}
                    isLoading={isFetchingDowntimeReasons}
                  />
                )}
              />
            </Col>
          )}
        </Row>
        <Row className={'footer-slide-pane'}>
          <Col sm="12">
            <Button.Ripple
              className="mr-1 mt-50 mb-50"
              color="primary"
              /*type="submit"*/
              onClick={handleSubmit(() => onSubmit())}
            >
              <FormattedMessage id="common.save" />
            </Button.Ripple>
            <Button.Ripple
              className="mt-50 mb-50"
              color="light"
              onClick={() => {
                reset();
                closeSlidingPane(false);
              }}
            >
              <FormattedMessage id="common.cancel" />
            </Button.Ripple>
          </Col>
        </Row>
      </Fragment>
      <SlidingPaneDowntimeReasons
        organization={organization}
        isOpenSlidingPane={downtimeReasonsSlideOpen}
        closeSlidingPaneDowntimeReasons={() => {
          openDowntimeReasonsSlide(false);
        }}
        width={width}
      />
    </SlidingPane>
  ) : (
    <></>
  );
};

SlidingPaneStartUnitDowntime.defaultProps = {
  closeSlidingPane: () => null,
  isSlidingPaneOpen: false,
  unit: {},
  organization: {}
};

export default SlidingPaneStartUnitDowntime;
